import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { getFullApiUrl } from "../../config";
import { getToken } from "../../utils";
import { Button, Icon } from "../../designSystem";
import Notice from "../ui/Notice";
import { LoadingPage } from "../LoadingPage";

const PLAN_FEATURES = [
  "全部付费节目，一集不落",
  "年度会员微信群",
  "会员专属线上讲座",
  "线下活动赠票抽选或 5 折",
];

const SubscriptionPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchPlans(), fetchPaymentStatus()]);
    setLoading(false);
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(
        getFullApiUrl("/api/v1/stripe/subscription-plans")
      );
      setPlans(response.data.plans || []);
    } catch (err) {
      console.error("Failed to fetch subscription plans:", err);
      setError("无法获取付费计划，请稍后重试");
    }
  };

  const fetchPaymentStatus = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        getFullApiUrl("/api/v1/subscribe/user_payment_status"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setPaymentStatus(response.data);
    } catch (err) {
      console.error("Failed to fetch payment status:", err);
      // For errors, set a default state
      setPaymentStatus({ has_paid: false });
    }
  };

  const handleSubscribe = async (priceId) => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    setProcessingPlanId(priceId);
    setError("");

    try {
      const token = getToken();
      const response = await axios.post(
        getFullApiUrl("/api/v1/stripe/create-checkout-session"),
        { price_id: priceId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError("创建支付会话失败，请重试");
      }
    } catch (err) {
      console.error("Failed to create checkout session:", err);
      setError(err.response?.data?.error || "创建支付会话失败，请重试");
    } finally {
      setProcessingPlanId(null);
    }
  };

  const formatPriceWithoutInterval = (amount, currency) => {
    const price = (amount / 100).toFixed(2);
    const currencySymbol = currency === "usd" ? "$" : "¥";
    return `${currencySymbol}${price}`;
  };

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <div className="lt-main">
      <div className="lt-mem-hero">
        <span className="kicker">MEMBERSHIP</span>
        <h1>选择付费方式</h1>
        <p>支持信用卡、Apple Pay、Google Pay 和支付宝。支付由 Stripe 处理，卡片信息不经过我们的服务器。</p>
      </div>

      {error ? (
        <Notice variant="error" style={{ maxWidth: 560, margin: "0 auto 20px" }}>
          {error}
        </Notice>
      ) : null}

      {paymentStatus && paymentStatus.has_paid ? (
        <div style={{ maxWidth: 560, margin: "0 auto", display: "grid", gap: 16, justifyItems: "center" }}>
          <Notice variant="success">
            你已经是付费用户，可以收听所有付费内容
            {paymentStatus.payment_source === "zsxq" && "（知识星球会员）"}
            {paymentStatus.payment_source === "stripe" && "（Stripe 订阅）"}
            。
          </Notice>
          <Button variant="secondary" onClick={() => navigate("/subscription/manage")}>
            管理订阅
          </Button>
        </div>
      ) : null}

      {paymentStatus && !paymentStatus.has_paid ? (
        <>
          {plans.length === 0 ? (
            <Notice variant="info" style={{ maxWidth: 560, margin: "0 auto" }}>
              暂无可用的付费计划。
            </Notice>
          ) : (
            <div className={`lt-plans ${plans.length === 1 ? "lt-plans--single" : ""}`}>
              {plans.map((plan) => (
                <div key={plan.id} className="bjl-plancard bjl-plancard--highlight">
                  <span className="bjl-plancard__flag">年度会员</span>
                  <div>
                    <div className="bjl-plancard__name">{plan.name}</div>
                    {plan.description ? (
                      <div className="bjl-plancard__desc">{plan.description}</div>
                    ) : null}
                  </div>
                  {plan.prices && plan.prices.length > 0 ? (
                    <div className="bjl-plancard__price">
                      <span className="bjl-plancard__amount">
                        {formatPriceWithoutInterval(
                          plan.prices[0].amount,
                          plan.prices[0].currency
                        )}
                      </span>
                      <span className="bjl-plancard__period">/年</span>
                    </div>
                  ) : null}
                  <ul className="bjl-plancard__features">
                    {PLAN_FEATURES.map((f) => (
                      <li key={f}>
                        <Icon name="check_circle" fill />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div style={{ display: "grid", gap: 10 }}>
                    {plan.prices &&
                      plan.prices.map((price) => (
                        <Button
                          key={price.id}
                          size="lg"
                          fullWidth
                          variant={price.interval === "one_time" ? "secondary" : "primary"}
                          icon={price.interval === "one_time" ? "account_balance_wallet" : "credit_card"}
                          loading={processingPlanId === price.id}
                          disabled={processingPlanId !== null}
                          onClick={() => handleSubscribe(price.id)}
                        >
                          {processingPlanId === price.id
                            ? "正在处理"
                            : price.interval === "one_time"
                            ? "支付宝支付"
                            : "信用卡支付"}
                        </Button>
                      ))}
                  </div>
                  <span className="bjl-plancard__foot">
                    <Icon name="lock" size={12} />
                    通过 Stripe 安全支付 · 随时可取消
                  </span>
                </div>
              ))}
            </div>
          )}

          <p
            style={{
              textAlign: "center",
              marginTop: 32,
              fontSize: "var(--text-sm)",
              color: "var(--text-3)",
            }}
          >
            已经是知识星球会员？
            <Button variant="ghost" size="sm" onClick={() => navigate("/zsxq")}>
              绑定知识星球账号
            </Button>
          </p>
        </>
      ) : null}
    </div>
  );
};

export default SubscriptionPlansPage;
